// custom-quick-add-hover.js
class QuickAddHover extends HTMLElement {
  constructor() {
    super();
    this.variantButtons = this.querySelectorAll('button.quick-add-hover__variant-button');
    this.variantsContainer = this.querySelector('.quick-add-hover__variants');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    
    this.setupScrollButtons();
    this.setupEventListeners();
    this.checkVariantsWidth();
    this.setupResizeObserver();
  }

  setupScrollButtons() {
    this.leftButton = document.createElement('button');
    this.leftButton.className = 'quick-add-hover__scroll-button quick-add-hover__scroll-button--left';
    this.leftButton.setAttribute('aria-label', 'Scroll variants left');
    this.leftButton.innerHTML = `<svg viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 13L1 7L7 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
    
    this.rightButton = document.createElement('button');
    this.rightButton.className = 'quick-add-hover__scroll-button quick-add-hover__scroll-button--right';
    this.rightButton.setAttribute('aria-label', 'Scroll variants right');
    this.rightButton.innerHTML = `<svg viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 13L7 7L1 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
    
    this.insertBefore(this.leftButton, this.variantsContainer);
    this.appendChild(this.rightButton);
    
    this.leftButton.style.display = 'none';
    this.rightButton.style.display = 'none';
  }

  setupEventListeners() {
    this.variantButtons.forEach(button => {
      button.addEventListener('click', this.handleQuickAdd.bind(this));
    });

    this.leftButton.addEventListener('click', () => this.scroll('left'));
    this.rightButton.addEventListener('click', () => this.scroll('right'));
    
    if (this.variantsContainer) {
      this.variantsContainer.addEventListener('scroll', () => {
        requestAnimationFrame(() => this.updateScrollButtonStates());
      });
    }
  }

  scroll(direction) {
    const scrollAmount = this.variantsContainer.offsetWidth * 0.4;
    const newScrollPosition = direction === 'left' 
      ? this.variantsContainer.scrollLeft - scrollAmount
      : this.variantsContainer.scrollLeft + scrollAmount;
    
    this.variantsContainer.scrollTo({
      left: newScrollPosition,
      behavior: 'smooth'
    });
  }

  updateScrollButtonStates() {
    if (!this.variantsContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.variantsContainer;
    const maxScroll = scrollWidth - clientWidth;
    
    this.leftButton.classList.toggle('disabled', scrollLeft <= 0);
    
    this.rightButton.classList.toggle('disabled', Math.abs(scrollLeft - maxScroll) < 1);
  }

  checkVariantsWidth() {
    if (!this.variantsContainer) return;
    
    const variantsWidth = Array.from(this.variantsContainer.children)
      .reduce((total, child) => {
        const style = getComputedStyle(child);
        return total + child.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
      }, 0);
    
    const gapWidth = parseFloat(getComputedStyle(this.variantsContainer).gap) * (this.variantsContainer.children.length - 1);
    const totalWidth = variantsWidth + gapWidth;

    const containerWidth = this.offsetWidth;
    const hasOverflow = totalWidth > containerWidth;

    if (hasOverflow) {
      this.variantsContainer.style.setProperty('justify-content', 'flex-start');
      this.classList.add('has-scroll-buttons');
      this.leftButton.style.display = 'flex';
      this.rightButton.style.display = 'flex';
      
      requestAnimationFrame(() => {
        this.updateScrollButtonStates();
      });
    } else {
      this.variantsContainer.style.setProperty('justify-content', 'center');
      this.classList.remove('has-scroll-buttons');
      this.leftButton.style.display = 'none';
      this.rightButton.style.display = 'none';
    }
  }

  setupResizeObserver() {
    const resizeObserver = new ResizeObserver(entries => {
      requestAnimationFrame(() => {
        this.checkVariantsWidth();
      });
    });

    resizeObserver.observe(this);
  }

  async handleQuickAdd(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.target;
    if (button.classList.contains('loading')) return;

    button.classList.add('loading');
    const spinner = this.createSpinner();
    
    const loadingContainer = document.createElement('div');
    loadingContainer.style.display = 'inline-flex';
    loadingContainer.style.alignItems = 'center';
    loadingContainer.style.gap = '0.5rem';
    
    loadingContainer.appendChild(document.createTextNode(button.textContent));
    loadingContainer.appendChild(spinner);
    
    const originalContent = button.innerHTML;
    button.innerHTML = '';
    button.appendChild(loadingContainer);

    try {
      const formData = new FormData();
      formData.append('id', button.dataset.variantId);
      formData.append('quantity', 1);

      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      if (this.cart && typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        console.error(data.description);
        button.classList.add('error');
        button.textContent = window.variantStrings?.addToCartError || 'Error';
        setTimeout(() => {
          button.classList.remove('error');
          button.innerHTML = originalContent;
        }, 2000);
        return;
      }

      button.classList.add('success');
      button.textContent = window.variantStrings?.addedToCart || 'Added';
      
      if (this.cart) {
        this.cart.renderContents(data);
        
        if (this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }
      } else {
        window.location = window.routes.cart_url;
        return;
      }

      setTimeout(() => {
        button.classList.remove('success');
        button.innerHTML = originalContent;
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.classList.add('error');
      button.textContent = window.variantStrings?.addToCartError || 'Error';
      setTimeout(() => {
        button.classList.remove('error');
        button.innerHTML = originalContent;
      }, 2000);
    } finally {
      button.classList.remove('loading');
    }
  }

  createSpinner() {
    const spinner = document.createElement('div');
    spinner.className = 'loading__spinner';
    const spinnerHtml = `
      <svg
        aria-hidden="true"
        focusable="false"
        class="spinner"
        viewBox="0 0 66 66"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    `;
    spinner.innerHTML = spinnerHtml;
    return spinner;
  }
}

customElements.define('quick-add-hover', QuickAddHover);